import { Component, inject, signal, computed, OnInit, OnDestroy, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Auth, authState } from '@angular/fire/auth';
import { take } from 'rxjs/operators';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { SpaceCardComponent } from '../space-card/space-card.component';
import { SiteFooterComponent } from '../site-footer/site-footer.component';
import { AssetService } from '../../services/asset.service';
import { Space, SpaceCategory, SPACE_CATEGORIES, SPACE_CATEGORY_LABELS, getSpaceCategories } from '../../models/asset.model';
import { spaceLocationLabel } from '../../shared/location/address-resolution.utils';
import { SeoService } from '../../services/seo.service';
import { buildExploreSeoTitle, SEO_DEFAULTS, truncateSeoText } from '../../shared/seo/seo.utils';

@Component({
    selector: 'app-explore',
    imports: [FormsModule, SpaceCardComponent, SiteFooterComponent, LucideAngularModule],
    providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
    templateUrl: './explore.component.html'
})
export class ExploreComponent implements OnInit, OnDestroy {
  private assetService = inject(AssetService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private auth = inject(Auth);
  private seo = inject(SeoService);

  /** Footer on public `/explorar`; shell routes use menu footer. */
  readonly showFooter = computed(() => !this.router.url.startsWith('/app/'));

  readonly allSpaces    = signal<Space[]>([]);
  readonly searchQuery  = signal('');
  readonly loading      = signal(true);

  // ── Filters ───────────────────────────────────────────────────────────────
  readonly filterCategory    = signal<SpaceCategory | ''>('');
  readonly filterProvince    = signal('');
  readonly filterMinCapacity = signal<number | null>(null);
  readonly filtersVisible    = signal(false);

  readonly SPACE_CATEGORIES       = SPACE_CATEGORIES;
  readonly SPACE_CATEGORY_LABELS  = SPACE_CATEGORY_LABELS;
  readonly DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  constructor() {
    effect(() => {
      const path = this.router.url.split('?')[0];
      if (path !== '/explorar' && path !== '/app/explorar') return;

      const prov = this.filterProvince();
      const provLabel = prov ? this.provinceLabel(prov) : undefined;
      const canonicalPath = prov ? `/explorar?prov=${encodeURIComponent(prov)}` : '/explorar';
      const description = provLabel
        ? truncateSeoText(
          `Encuentra y reserva salas y locales en ${provLabel} por horas. Fiestas, eventos y celebraciones con reserva segura.`,
        )
        : SEO_DEFAULTS.defaultDescription;

      this.seo.setPage({
        title: buildExploreSeoTitle(provLabel),
        description,
        canonicalPath,
      });
    });
  }

  ngOnDestroy(): void {
    this.seo.resetToDefault();
  }

  /** Unique sorted province slugs from loaded spaces (used for the province dropdown). */
  readonly availableProvinces = computed<string[]>(() => {
    const seen = new Set<string>();
    for (const s of this.allSpaces()) {
      const p = s.slugProvincia?.trim();
      if (p) seen.add(p);
    }
    return Array.from(seen).sort();
  });

  /** Province label prettified from slug (e.g. "madrid" → "Madrid"). */
  provinceLabel(slug: string): string {
    return slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ');
  }

  toggleFilters(): void { this.filtersVisible.update(v => !v); }

  /** Safe category label — returns empty string for the "all" sentinel. */
  categoryLabel(cat: SpaceCategory | ''): string {
    return cat ? SPACE_CATEGORY_LABELS[cat] : '';
  }

  readonly activeFilterCount = computed(() => {
    let n = 0;
    if (this.filterCategory()) n++;
    if (this.filterProvince()) n++;
    if (this.filterMinCapacity() !== null) n++;
    return n;
  });

  readonly filteredSpaces = computed(() => {
    let spaces = this.allSpaces();

    // Text search
    const q = this.searchQuery().trim().toLowerCase();
    if (q) {
      spaces = spaces.filter(s =>
        s.name.toLowerCase().includes(q) ||
        spaceLocationLabel(s).toLowerCase().includes(q) ||
        (s.description ?? '').toLowerCase().includes(q),
      );
    }

    // Category filter
    const cat = this.filterCategory();
    if (cat) {
      spaces = spaces.filter(s => getSpaceCategories(s).includes(cat));
    }

    // Province filter
    const prov = this.filterProvince();
    if (prov) {
      spaces = spaces.filter(s => s.slugProvincia === prov);
    }

    // Minimum capacity filter
    const minCap = this.filterMinCapacity();
    if (minCap !== null && minCap > 0) {
      spaces = spaces.filter(s => (s.capacity ?? 0) >= minCap);
    }

    return spaces;
  });

  clearFilters(): void {
    this.filterCategory.set('');
    this.filterProvince.set('');
    this.filterMinCapacity.set(null);
  }

  ngOnInit(): void {
    const q    = this.route.snapshot.queryParamMap.get('q');
    const cat  = (
      this.route.snapshot.queryParamMap.get('cat') ??
      this.route.snapshot.queryParamMap.get('categoria')
    ) as SpaceCategory | null;
    const prov = this.route.snapshot.queryParamMap.get('prov');

    if (q)    this.searchQuery.set(q);
    if (cat && SPACE_CATEGORIES.includes(cat)) this.filterCategory.set(cat);
    if (prov) this.filterProvince.set(prov);

    if (this.router.url.split('?')[0] === '/explorar') {
      authState(this.auth).pipe(take(1)).subscribe(user => {
        if (user?.emailVerified) {
          void this.router.navigate(['/app/explorar'], {
            queryParams: { ...(q ? { q } : {}), ...(cat ? { cat } : {}), ...(prov ? { prov } : {}) },
          });
        }
      });
    }

    this.assetService.getAllSpaces().subscribe({
      next: spaces => { this.allSpaces.set(spaces); this.loading.set(false); },
      error: ()    => { this.loading.set(false); },
    });
  }
}
